import { ApiRequest } from 'framework/api';
import { FormBuilderValue } from 'controls/react/form/formBuilderNew/models';
import { AnnotationValidationMethod, TestResult } from 'controls/react/form/formBuilderNew/annotationValidator';

export interface QualifierIdentity {
	serviceId: string;
	elementId: string;
	qualifierId: string;
}

// same endpoint as the old QualifierAPI.getCommonData, rebuilt as an ApiRequest for the formBuilderNew
// FormBuilderStore's getPropertyData hook
export function getQualifierPropertyData(identity: QualifierIdentity, propertyPath: string, value: FormBuilderValue) {
	return new ApiRequest<string | { text: string; value: string }[]>({
		url: `services/${identity.serviceId}/elements/${identity.elementId}/qualifiers/${identity.qualifierId}/properties/${propertyPath}/data`,
		method: 'POST',
		accountBased: true,
		payload: value,
	});
}

// same endpoints as the old QualifierAPI.validate/getTest, rebuilt as ApiRequests for FormBuilderStore's
// validateMethods hook
function validateEndpoint(method: AnnotationValidationMethod) {
	return (identity: QualifierIdentity, value: FormBuilderValue) => {
		return new ApiRequest<TestResult>({
			url: `services/${identity.serviceId}/elements/${identity.elementId}/qualifiers/${identity.qualifierId}/${method}`,
			method: 'POST',
			accountBased: true,
			payload: { values: value },
		});
	};
}

export const postValidateQualifier = validateEndpoint('postValidate');
export const preValidateQualifier = validateEndpoint('preValidate');
export const testQualifier = validateEndpoint('test');

// GET variant of the test endpoint above — re-fetches the last persisted test result for an already-saved
// qualifier without resubmitting current form values (used by externalConfigErrorWindow, which shows results
// for a qualifier outside the wizard's own edit session).
export function getQualifierTestResult(identity: QualifierIdentity) {
	return new ApiRequest<TestResult>({
		url: `services/${identity.serviceId}/elements/${identity.elementId}/qualifiers/${identity.qualifierId}/test`,
		accountBased: true,
	});
}

export interface QualifierRootData {
	id: string;
	className: string;
	name: string;
	description: string;
	agentId: string;
	agentStatus?: string;
	shared: boolean;
	state: string;
	templateId?: string;
	externalId?: string;
	lazyData?: boolean;
	independentForms?: boolean;
	// asset type restriction for the qualifier's "Target" asset picker's inline "create new asset" window —
	// ported from the old formBuilder's connectDependencies.js reading `original.systemType`.
	systemType?: string;
	configuration: {
		configurationName: string;
		configurationDescription: string;
		forms: { components: string[] }[];
		properties: unknown[];
		values: FormBuilderValue;
		warning: unknown;
		hasTest: boolean;
		hasPreValidate: boolean;
		hasPostValidate: boolean;
	};
}

export function loadQualifierTemplate(serviceId: string, templateId: string) {
	return new ApiRequest<QualifierRootData>({
		url: `services/${serviceId}/mxmodel/qualifiers/templates/${templateId}`,
		accountBased: true,
	});
}

export function loadQualifier(identity: QualifierIdentity) {
	return new ApiRequest<QualifierRootData>({
		url: `services/${identity.serviceId}/elements/${identity.elementId}/qualifiers/${identity.qualifierId}`,
		accountBased: true,
	});
}

export function loadQualifierClipboard(serviceId: string) {
	return new ApiRequest<QualifierRootData>({
		url: `services/${serviceId}/qualifiers/clipboard`,
		accountBased: true,
	});
}

export function copyQualifier(serviceId: string, qualifierId: string) {
	return new ApiRequest<QualifierRootData>({
		url: `services/${serviceId}/qualifiers/${qualifierId}/servicequalifier`,
		accountBased: true,
	});
}

export function loadQualifierHelp(serviceId: string, className: string) {
	return new ApiRequest<string>({
		url: `services/${serviceId}/qualifierTemplates/dcc/${className}/documentation`,
		accountBased: true,
	});
}

export type QualifierAsset = {
	id: string;
	name: string;
	targets?: { id: string; address: string }[];
};

export type QualifierAssetType = 'GENERIC' | 'UNIX' | 'WINDOWS';

export function loadQualifierAssets(assetType: QualifierAssetType = 'GENERIC') {
	return new ApiRequest<QualifierAsset[]>({
		url: `assets${assetType === 'GENERIC' ? '' : `/systemTypes/${assetType}`}?ignoreTags=true`,
		accountBased: true,
	});
}

export function getService(serviceId: string) {
	return new ApiRequest<{ operatingSchedule?: { timeZone?: string } }>({
		url: `services/${serviceId}`,
		accountBased: true,
	});
}
